import { useTranslation } from 'react-i18next';
import { Lock, MessageSquareText, X } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import { Button } from '@repo/ui/src/components/ui/button';
import type { AttendanceStatus, SessionParticipant } from '@repo/shared-types';
import { useOrgSettings } from '../hooks/useOrgSettings';
import { excusedQuotaDisplay, absenceChargeNoteKey } from '../lib/attendanceQuota';
import { fmtLockedMonth } from '../lib/attendanceLock';
import { StudentNamePopover } from './StudentNamePopover';
import { AdminOnlyTooltip } from './AdminOnlyTooltip';
import { QuotaExplanationPopover } from './QuotaExplanationPopover';
import { AdhocExplanationPopover, ModeExplanationPopover } from './ModeExplanationPopover';

/**
 * One student, one session, attendance controls — shared by the workbench
 * card and session detail so the two stop drifting in colour, type size and
 * badge noise. See docs/superpowers/specs/2026-08-08-attendance-surfaces-design.md §2.
 *
 * Below `sm` the name and the buttons stack instead of fighting over 375px —
 * a real Vietnamese name needs its full tail to distinguish siblings (e.g.
 * "Nguyễn Cát Hồng Ân" vs "...Thiên Ân"), so it is never truncated. From `sm`
 * up there is room for one row; only there does a pathologically long name
 * get a soft right-edge fade instead of an ellipsis.
 */
export function AttendanceRow({
  participant,
  current,
  started,
  onMark,
  onRemove,
  adhocBadge,
  removeDisabled,
  removeGuarded,
  hasNote,
  onOpenNote,
}: {
  participant: SessionParticipant;
  current: AttendanceStatus;
  started: boolean;
  onMark: (next: AttendanceStatus) => void;
  /** Session detail only — the workbench marks rather than manages the roster. */
  onRemove?: () => void;
  /**
   * Session detail only. Deliberately independent of `onRemove`: whether a
   * row can be removed is a permission/mode question, whether it shows
   * "Đột xuất" is a data question (`participant.fromSlot === false`).
   * Overloading one prop's presence to answer both would be dishonest about
   * what each actually controls, so callers pass this explicitly.
   */
  adhocBadge?: boolean;
  /** Disables the remove button while a removal is in flight, or when the
   *  viewer lacks permission. Only meaningful alongside `onRemove`. */
  removeDisabled?: boolean;
  /** Wraps the remove button in the RBAC "admins only" tooltip. */
  removeGuarded?: boolean;
  /** THI-105: this student has a personal note on this session. */
  hasNote?: boolean;
  /** Opens the notes panel with THIS student expanded. Wired from both
   *  session detail and the workbench card. */
  onOpenNote?: () => void;
}): React.ReactElement {
  const { t } = useTranslation('scheduling');

  // Cached (staleTime 5min) — both call sites (workbench card, session
  // detail) render many rows, and this dedupes to one request regardless.
  const { data: orgSettings, isPending: orgSettingsPending } = useOrgSettings();
  const limit = orgSettings?.maxExcusedPerMonth ?? null;
  const { badge, overLimit, madeUpShown } = excusedQuotaDisplay(participant, limit);
  const noteKey = absenceChargeNoteKey(participant);

  // The month this row belongs to has been chốt sổ: saving it would be
  // refused by the attendance route's I5 guard with a 409, and
  // `attendanceLocked` is computed from exactly that predicate — so the row
  // says so up front instead of letting the teacher tap and lose the change.
  const locked = participant.attendanceLocked;

  const toggle = (next: AttendanceStatus) => onMark(current === next ? 'PENDING' : next);

  const removeButton = onRemove && (
    // Spacing lives on the Button itself, not on AdminOnlyTooltip's wrapper —
    // that wrapper only renders when `show` is true, so a class passed to it
    // vanishes for the owner/admin who can actually press this button.
    <AdminOnlyTooltip show={!!removeGuarded}>
      <Button
        variant="ghost"
        size="sm"
        className="ml-2 shrink-0"
        aria-label={t('session.removeStudent')}
        disabled={removeDisabled}
        onClick={onRemove}
      >
        <X className="h-4 w-4" />
      </Button>
    </AdminOnlyTooltip>
  );

  // Same over-limit destructive swap for both branches: mirrors the ABSENT
  // button's selected style when the Excused button itself is selected, and
  // falls back to a plain destructive outline when it isn't — in both cases
  // this replaces (not adds to) the normal attention scheme, since a row
  // that is over quota is never merely "attention", it's a decision point.
  const excusedButtonClassName = overLimit
    ? current === 'EXCUSED'
      ? 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive'
      : 'border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive'
    : current === 'EXCUSED' && 'border-attention bg-attention text-attention-foreground hover:bg-attention';

  // Cold-start flicker (design doc 2026-08-22-states §3): `limit` is null
  // until `useOrgSettings` resolves, so `excusedQuotaDisplay` would already
  // compute a plain-count badge from the participant's raw excused/made-up
  // counts before the org's real cap is known — then flip to "n/limit" the
  // instant it loads. One paint: show the bare label with no badge/suffix
  // while pending (overLimit is guaranteed false in that window too, since
  // `quotaApplies` requires a non-null limit, so the destructive styling
  // above never fires here either).
  // The quota fragment as ONE string ("2/3", "2/3 (đã bù 1)"), composed once
  // and handed to `QuotaExplanationPopover` as the chip's text. `badge` is
  // never null while `madeUpShown` is non-null — excusedQuotaDisplay forces
  // "0" in exactly that case — so this can never produce a stray leading
  // separator.
  const quotaSuffix = orgSettingsPending
    ? null
    : [badge, madeUpShown != null ? t('attendance.madeUpSuffix', { count: madeUpShown }) : null]
        .filter(Boolean)
        .join(' ') || null;

  // One slot, mutually exclusive by `current`: the quota warning only makes
  // sense while the row is actually marked EXCUSED and over the cap, and the
  // charge note only makes sense once it's marked ABSENT — never both at
  // once, so a plain if/else-if is enough.
  // Locked wins the slot: while the month is closed none of the other notes
  // is actionable — the row cannot change at all — and the reason it cannot
  // is the only thing worth a line here.
  // The month is what makes the note actionable ("mở lại tháng 7/2026"), but
  // `lockedPeriodLabel` is nullable — rather than render "tháng  —" with a
  // hole in it, the label-less case gets its own sentence.
  const lockedMonth = fmtLockedMonth(participant.lockedPeriodLabel);
  // 48px full-width thirds on phones (design §1). Pinned in px: --spacing is
  // 0.2rem app-wide, so `h-12` would render 38.4px. `basis-0` alongside
  // `flex-1` makes the three exactly equal regardless of label width. From
  // `sm` the row goes back to today's natural-width inline buttons — except on
  // a coarse pointer, where the target stays 48px at any width (01-shell §4);
  // `min-h` rather than `h` so it beats `sm:h-8` on property semantics, not on
  // variant sort order. No `flex-col`/`flex-row` split any more: the quota
  // fragment moved out to `QuotaExplanationPopover`, so the label is single-line
  // at every width now.
  const statusButtonClass =
    'h-[48px] flex-1 basis-0 text-[13.5px] touch:min-h-[48px] ' +
    'sm:h-8 sm:flex-none sm:basis-auto sm:text-xs';

  const note = locked ? (
    <p className="text-xs text-surface-500">
      {lockedMonth ? t('attendance.locked', { month: lockedMonth }) : t('attendance.lockedNoMonth')}
    </p>
  ) : current === 'EXCUSED' && overLimit ? (
      <p className="text-xs text-destructive">{t('attendance.quotaWarning', { limit })}</p>
    ) : current === 'ABSENT' ? (
      <p className="text-xs text-surface-500">{t(`attendance.chargeNote.${noteKey}`)}</p>
    ) : null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-surface-200 bg-surface-0 px-3 py-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          {/* No truncation and no fade. The fade was designed for a one-line
              layout we abandoned: once the name owns its own row on a phone and
              the row is wide at `sm`, there is nothing to cut — and the mask was
              applied unconditionally, so short names were dimmed with space to
              spare. `whitespace-nowrap` goes with it: an unusually long name now
              wraps to a second line instead of shoving the buttons out. */}
          <StudentNamePopover studentId={participant.studentId}>
            <span className="block text-left text-[13.5px] font-medium text-surface-800">
              {/* Nickname is phone-only: the desktop row is untouched from
                  before beat 5, this row already has the popover for detail,
                  and `sm:hidden`/`hidden sm:inline` keeps a screen reader from
                  hearing the name twice. */}
              <span className="sm:hidden">
                {participant.studentName}
                {/* Muted, in parentheses, on one line with the name — this is
                    the word the teacher actually calls the child, and beat
                    5's whole premise is that it is what they search by. */}
                {participant.studentNickname && (
                  <span className="font-normal text-surface-500"> ({participant.studentNickname})</span>
                )}
              </span>
              <span className="hidden sm:inline">{participant.studentName}</span>
            </span>
          </StudentNamePopover>
          {onOpenNote && (
            /* Inside the NAME group, never in the right-hand chip cluster:
               those chips are talking about money (hình thức, Đột xuất). A
               28×28 hit area with -my-1.5 so the row's height does not
               change. */
            <button
              type="button"
              onClick={onOpenNote}
              aria-label={t('session.noteFor', { name: participant.studentName })}
              className="-my-1.5 flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-sm text-brand-600 hover:bg-brand-50"
            >
              <MessageSquareText
                className={cn('h-[15px] w-[15px]', !hasNote && 'text-surface-300')}
                strokeWidth={1.8}
              />
            </button>
          )}
          {quotaSuffix && (
            <QuotaExplanationPopover
              studentName={participant.studentName}
              quota={quotaSuffix}
              participant={participant}
              limit={limit}
              overLimit={overLimit}
            />
          )}
          {/* The badge cluster — right-aligned, NOT absolutely positioned.
              Absolute was rejected: a long Vietnamese name deliberately wraps
              here (siblings differ only in the tail), and an out-of-flow
              cluster would sit on top of the second line. `ml-auto` inside the
              flow means the name simply gets less width and wraps under it,
              which can never overlap. On a phone the name group is full width
              so the cluster lands at the row's right edge; from `sm` the group
              is content-sized, `ml-auto` has no free space to take, and the
              cluster falls in naturally at the end of the name group — just
              before the status buttons. Both are the intended placement.

              The mode badge is ALWAYS the rightmost anchor: it is on every row,
              so a fixed right edge is what makes the column scannable. Đột xuất
              is conditional and therefore sits second-from-right, where its
              appearing and disappearing moves nothing the eye is tracking. */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {adhocBadge && <AdhocExplanationPopover studentName={participant.studentName} />}
            <ModeExplanationPopover
              studentName={participant.studentName}
              mode={participant.mode}
              termIndex={participant.termIndex}
              termSize={participant.termSize}
            />
          </div>
        </div>

        {started ? (
          <div className="flex items-center gap-2 sm:gap-1">
            {/* The glyph carries no information the note below doesn't spell
                out, so it is decorative to a screen reader — the buttons'
                own `aria-disabled` is what announces the state. */}
            {locked && <Lock className="mr-0.5 h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />}
            <Button
              variant={current === 'PRESENT' ? 'default' : 'outline'}
              className={cn(statusButtonClass, current === 'PRESENT' && 'border-brand-500 bg-brand-500 text-white hover:bg-brand-500')}
              size="sm"
              disabled={locked}
              aria-disabled={locked}
              onClick={() => toggle('PRESENT')}
            >
              {t('attendance.present')}
            </Button>
            <Button
              variant={current === 'EXCUSED' ? 'default' : 'outline'}
              className={cn(statusButtonClass, excusedButtonClassName)}
              size="sm"
              disabled={locked}
              aria-disabled={locked}
              onClick={() => toggle('EXCUSED')}
            >
              {t('attendance.excused')}
            </Button>
            <Button
              variant={current === 'ABSENT' ? 'default' : 'outline'}
              className={cn(statusButtonClass, current === 'ABSENT' && 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive')}
              size="sm"
              disabled={locked}
              aria-disabled={locked}
              onClick={() => toggle('ABSENT')}
            >
              {t('attendance.absent')}
            </Button>
            {removeButton}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {locked && <Lock className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />}
            <span className="text-xs text-surface-400">{t('attendance.upcoming')}</span>
            <Button
              variant={current === 'EXCUSED' ? 'default' : 'outline'}
              // Three buttons sharing a row look deliberate stretched; one
              // button spanning the whole width just looks broken. The upcoming
              // case keeps its natural size.
              className={cn(excusedButtonClassName)}
              size="sm"
              disabled={locked}
              aria-disabled={locked}
              onClick={() => toggle('EXCUSED')}
            >
              {t('attendance.excused')}
            </Button>
            {removeButton}
          </div>
        )}
      </div>

      {note}
    </div>
  );
}
